import React from 'react';
import { Box, Typography, Grid, Card, CardContent, Chip } from '@mui/material';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

// Asset types (use keys for translatability)
const assets = [
  'asset.parking', 'asset.wedding_halls', 'asset.residential_villas',
  'asset.trade_fairs', 'asset.schools', 'asset.custody_division',
  'asset.offices', 'asset.shopping_mall'
];

// Stat card
const StatCard = ({ value, label, suffix = '' }) => {
  const { t } = useTranslation();

  return (
    <motion.div
      whileHover={{ scale: 1.03 }}
      transition={{ duration: 0.3 }}
      style={{ flex: 1, minWidth: 220 }}
    >
      <Card
        elevation={10}
        sx={{
          background: 'linear-gradient(145deg, #1e90ff, #63b3ed)',
          color: '#fff',
          borderRadius: '20px',
          textAlign: 'center',
          p: 3,
        }}
      >
        <CardContent>
          <Typography variant="h4" fontWeight="bold">
            {value.toLocaleString()}
            <span style={{ fontSize: '0.8em', color: '#d0f0ff' }}>{suffix}</span>
          </Typography>
          <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>{t(label)}</Typography>
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Asset chip
const AssetChip = ({ label }) => {
  const { t } = useTranslation();
  return (
    <motion.div whileHover={{ scale: 1.1 }} style={{ margin: 6 }}>
      <Chip
        label={t(label)}
        sx={{
          backgroundColor: '#e3f2fd',
          color: '#1e88e5',
          fontWeight: 'bold',
          fontSize: '0.9rem',
          px: 2,
          py: 1.5,
          borderRadius: '12px',
          boxShadow: '0px 4px 10px rgba(30, 144, 255, 0.2)'
        }}
      />
    </motion.div>
  );
};

const AssetManagement = () => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        minHeight: '85vh',
        p: { xs: 3, md: 6 },
        background: 'white',
        backgroundAttachment: 'fixed',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      {/* Title */}
      <Typography variant="h3" fontWeight="bold" color="primary" gutterBottom textAlign="center">
        {t('asset_management.title')}
      </Typography>

      {/* Number of assets */}
      <Box textAlign="center" mt={4} mb={6}>
        <Typography variant="h2" fontWeight="bold" display="flex" justifyContent="center" alignItems="center" flexWrap="wrap">
          <Box component="span" color="#1e90ff">18</Box>
          <Box component="span" fontSize="2rem" mx={1}>←</Box>
          <Box component="span" color="#333">19</Box>
          <Box component="span" fontSize="2rem" mx={2}>+</Box>
          <Box component="span" color="#1e90ff">10</Box>
        </Typography>
        <Typography variant="h6" mt={1}>{t('asset_management.asset_count')}</Typography>
        <Typography variant="subtitle2" color="textSecondary">{t('asset_management.locations')}</Typography>
      </Box>

      {/* Asset types */}
      <Typography variant="h5" fontWeight="bold" mb={2} textAlign="center">
        {t('asset_management.asset_types')}
      </Typography>
      <Box display="flex" flexWrap="wrap" justifyContent="center" mb={5}>
        {assets.map((item, index) => (
          <AssetChip key={index} label={item} />
        ))}
      </Box>

      {/* Stat cards */}
      <Grid container spacing={4} justifyContent="center">
        <Grid item xs={12} md={6}>
          <StatCard label="asset_management.final_value" value={3837000} suffix="K" />
        </Grid>
        <Grid item xs={12} md={6}>
          <StatCard label="asset_management.total_area" value={38000} suffix="K" />
        </Grid>
      </Grid>
    </Box>
  );
};

export default AssetManagement;
